import Panel from 'components/Antd/Layout/Panel/index';
import React from 'react';
import { Button, Space } from 'antd';
import { ReactComponent as ProductTestingIcon } from 'assets/icons/Nav/ProductTesting.svg';
import { ReactComponent as ProductManagementIcon } from 'assets/icons/Nav/ProductManagement.svg';
import { ReactComponent as DocumentManagementIcon } from 'assets/icons/Nav/DocumentDark.svg';
import Icon from '@ant-design/icons';
import { Field } from 'formik';
import QMASTable from 'components/Molecules/Table/index';
import classes from './Compliance.module.scss';
import TestData from './components/TestData/index';
import CertificateData from './components/CertificateData/index';
import OtherData from './components/OtherData/index';
import { TEST_DATA_COL_DEFS, CERTIFICATE_DATA_COL_DEFS, OTHER_DATA_COL_DEFS } from './colDefs';
import { QMASDocumentContext } from '../../context';

export const ComplianceData = () => {
  const { readonly } = React.useContext(QMASDocumentContext);
  const refTestData = React.useRef();
  const refCertificationData = React.useRef();
  const refOtherData = React.useRef();
  const [showingForm, setShowingForm] = React.useState(null);
  return (
    <div className={classes.compliance}>
      <Panel>
        <Field name="complianceData.testData">
          {({ field }) => {
            const { value } = field;
            return (
              <div className={classes.content}>
                <div className={classes.title}>
                  <Space>
                    <Icon component={ProductTestingIcon} />
                    <span className={classes.title}>Test Data</span>
                  </Space>
                  <TestData
                    wrapClassName={classes.overlay}
                    childRef={refTestData}
                    title={
                      <Space>
                        <Icon component={ProductTestingIcon} />
                        <span className={classes.title}>Edit Test Data</span>
                      </Space>
                    }
                    destroyOnClose
                    width={1100}
                    initialValues={
                      Array.isArray(value) && value.length
                        ? { testData: value }
                        : {
                            testData: [
                              {
                                countryListId: null,
                                status: null,
                                testItemId: null,
                                reportId: null,
                                serviceProviderId: null,
                                expiryDate: undefined,
                              },
                            ],
                          }
                    }
                    cancelButtonProps={{ hidden: true }}
                    okText="Confirm"
                    onTrigger={() => {
                      setShowingForm('test-data');
                    }}
                    triggerElement={
                      <Button hidden={readonly} className={classes.edit} type="link">
                        Edit
                      </Button>
                    }
                    open={showingForm === 'test-data'}
                    onOk={() => {
                      refTestData.current.submit();
                    }}
                    onCancel={() => {
                      setShowingForm(null);
                    }}
                    onSubmit={(values) => {
                      field.onChange({
                        target: {
                          name: 'complianceData.testData',
                          value: values.testData,
                        },
                      });
                      setShowingForm(null);
                    }}
                  />
                </div>
                {field.value && field.value.length > 0 && (
                  <QMASTable
                    pagination={false}
                    dataSource={field.value || []}
                    scroll={{ x: 'max-content' }}
                    columns={TEST_DATA_COL_DEFS}
                  />
                )}
              </div>
            );
          }}
        </Field>
      </Panel>

      <Panel title="Certificate Data">
        <Field name="complianceData.certificateData">
          {({ field }) => {
            const { value } = field;
            return (
              <div className={classes.content}>
                <div className={classes.title}>
                  <Space>
                    <Icon component={ProductManagementIcon} />
                    <span className={classes.title}>Certificate Data</span>
                  </Space>
                  <CertificateData
                    childRef={refCertificationData}
                    wrapClassName={classes.overlay}
                    title={
                      <Space>
                        <Icon component={ProductManagementIcon} />
                        <span className={classes.title}>Edit Certificate Data</span>
                      </Space>
                    }
                    destroyOnClose
                    width={1100}
                    initialValues={
                      Array.isArray(value) && value.length
                        ? { certificateData: value }
                        : {
                            certificateData: [
                              {
                                customIssuePartyId: null,
                                customCertificateScoreId: null,
                                customCertificateTypeId: null,
                                expiryDate: undefined,
                              },
                            ],
                          }
                    }
                    cancelButtonProps={{ hidden: true }}
                    okText="Confirm"
                    onTrigger={() => {
                      setShowingForm('certification-data');
                    }}
                    triggerElement={
                      <Button hidden={readonly} className={classes.edit} type="link">
                        Edit
                      </Button>
                    }
                    open={showingForm === 'certification-data'}
                    onOk={() => {
                      refCertificationData.current.submit();
                    }}
                    onCancel={() => {
                      setShowingForm(null);
                    }}
                    onSubmit={(values) => {
                      field.onChange({
                        target: {
                          name: 'complianceData.certificateData',
                          value: values.certificateData,
                        },
                      });
                      setShowingForm(null);
                    }}
                  />
                </div>
                {field.value && field.value.length > 0 && (
                  <QMASTable
                    pagination={false}
                    dataSource={field.value || []}
                    scroll={{ x: 'max-content' }}
                    columns={CERTIFICATE_DATA_COL_DEFS}
                  />
                )}
              </div>
            );
          }}
        </Field>
      </Panel>
      <Panel title="Other">
        <Field name="complianceData.otherData">
          {({ field }) => {
            const { value } = field;
            return (
              <div className={classes.content}>
                <div className={classes.title}>
                  <Space>
                    <Icon component={DocumentManagementIcon} />
                    <span className={classes.title}>Other</span>
                  </Space>
                  <OtherData
                    childRef={refOtherData}
                    wrapClassName={classes.overlay}
                    title={
                      <Space>
                        <Icon component={DocumentManagementIcon} />
                        <span className={classes.title}>Other</span>
                      </Space>
                    }
                    destroyOnClose
                    width={1100}
                    triggerElement={
                      <Button hidden={readonly} className={classes.edit} type="link">
                        Edit
                      </Button>
                    }
                    initialValues={
                      Array.isArray(value) && value.length
                        ? { otherData: value }
                        : {
                            otherData: [
                              {
                                result: null,
                                customComplianceOtherDataTypeId: null,
                                customIssuePartyId: null,
                                expiryDate: undefined,
                                name: null,
                                description: null,
                              },
                            ],
                          }
                    }
                    cancelButtonProps={{ hidden: true }}
                    okText="Confirm"
                    onTrigger={() => {
                      setShowingForm('other-data');
                    }}
                    open={showingForm === 'other-data'}
                    onOk={() => {
                      refOtherData.current.submit();
                    }}
                    onCancel={() => {
                      setShowingForm(null);
                    }}
                    onSubmit={(values) => {
                      field.onChange({
                        target: {
                          name: 'complianceData.otherData',
                          value: values.otherData,
                        },
                      });
                      setShowingForm(null);
                    }}
                  />
                </div>
                {field.value && field.value.length > 0 && (
                  <QMASTable
                    pagination={false}
                    dataSource={field.value || []}
                    scroll={{ x: 'max-content' }}
                    columns={OTHER_DATA_COL_DEFS}
                  />
                )}
              </div>
            );
          }}
        </Field>
      </Panel>
    </div>
  );
};
